import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { leadsAPI } from '../../utils/api';

const PRIORITIES = [
  { value: 'hot', label: '🔥 Hot' },
  { value: 'warm', label: '🌡 Warm' },
  { value: 'cold', label: '❄️ Cold' },
];

const VISA_TYPES = [
  'Visitor Visa', 'Student Visa', 'Permanent Residency',
  'Investor Visa', 'Startup Visa', 'Dependent Visa', 'Work Permit',
];

export default function LeadEditDrawer({ lead, open, onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    whatsapp: '',
    nationality: '',
    country_of_interest: '',
    visa_type: '',
    product_interest: '',
    quoted_price: '',
    discount: '',
    priority: 'warm',
    follow_up_date: '',
    notes: '',
    tags: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setForm({
        full_name: lead.full_name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        whatsapp: lead.whatsapp || '',
        nationality: lead.nationality || '',
        country_of_interest: lead.country_of_interest || '',
        visa_type: lead.visa_type || '',
        product_interest: lead.product_interest || '',
        quoted_price: lead.quoted_price != null ? String(lead.quoted_price) : '',
        discount: lead.discount != null ? String(lead.discount) : '',
        priority: lead.priority || 'warm',
        follow_up_date: lead.follow_up_date ? lead.follow_up_date.slice(0, 10) : '',
        notes: lead.notes || '',
        tags: Array.isArray(lead.tags) ? lead.tags.join(', ') : (lead.tags || ''),
      });
    }
  }, [lead]);

  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const handleSave = async () => {
    if (!form.full_name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quoted_price: form.quoted_price ? parseFloat(form.quoted_price) : null,
        discount: form.discount ? parseFloat(form.discount) : null,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        follow_up_date: form.follow_up_date || null,
      };
      await leadsAPI.updateLead(lead.id, payload);
      toast.success('Lead updated successfully');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Edit Lead — {lead?.full_name}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          {/* Personal Info */}
          <div className="col-span-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Personal Information
            </h3>
          </div>

          <div className="space-y-1.5">
            <Label>Full Name <span className="text-rose-500">*</span></Label>
            <Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>WhatsApp</Label>
            <Input value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Nationality</Label>
            <Input value={form.nationality} onChange={(e) => set('nationality', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Country of Interest</Label>
            <Input value={form.country_of_interest} onChange={(e) => set('country_of_interest', e.target.value)} />
          </div>

          {/* Interest */}
          <div className="col-span-2 pt-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Interest & Pricing
            </h3>
          </div>

          <div className="space-y-1.5">
            <Label>Visa Type</Label>
            <Select value={form.visa_type} onValueChange={(v) => set('visa_type', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select visa type" />
              </SelectTrigger>
              <SelectContent>
                {VISA_TYPES.map((vt) => (
                  <SelectItem key={vt} value={vt}>{vt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Product / Service</Label>
            <Input value={form.product_interest} onChange={(e) => set('product_interest', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Quoted Price (INR)</Label>
            <Input type="number" value={form.quoted_price} onChange={(e) => set('quoted_price', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Discount (INR)</Label>
            <Input type="number" value={form.discount} onChange={(e) => set('discount', e.target.value)} />
          </div>

          {/* Sales fields */}
          <div className="col-span-2 pt-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Sales Management
            </h3>
          </div>

          <div className="space-y-1.5">
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Follow-up Date</Label>
            <Input type="date" value={form.follow_up_date} onChange={(e) => set('follow_up_date', e.target.value)} />
          </div>

          <div className="col-span-2 space-y-1.5">
            <Label>Tags (comma separated)</Label>
            <Input
              value={form.tags}
              onChange={(e) => set('tags', e.target.value)}
              placeholder="e.g. Canada, Express Entry, High Budget"
            />
          </div>

          <div className="col-span-2 space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Any additional notes about this lead..."
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
